'use client';

import dynamic from 'next/dynamic';
import React, { Fragment } from 'react';
import { PageLayout } from '@/_pages/page-layout/ui/page-layout';
import { Alergens } from '@/entities/product/ui/alergens';
import * as Categories from '@/entities/product/ui/categories-new';
import { MenuItemCard } from '@/entities/product/ui/menu-item';
import * as ProductCard from '@/entities/product/ui/product-card';
import ProductDetailComponent, * as ProductDetailParts from '@/entities/product/ui/product-detail';
import * as ProductDetailModal from '@/entities/product/ui/product-detail/product-detail-v2';
import { ProductMenuNote } from '@/entities/product/ui/product-detail/product-menu-note';
import { RecommendedCarousel } from '@/entities/product/ui/recommended-carousel';
import { CurrentVenueName } from '@/entities/venue/ui/current-venue-name';
import { CurrentVenueStatus } from '@/entities/venue/ui/current-venue-status';
import * as VenueAddress from '@/entities/venue/ui/venue-address';
import * as VenueAddressAction from '@/entities/venue/ui/venue-address-action';
import * as Venue from '@/entities/venue/ui/venue-card/venue-card-atoms';
import * as VenueTelephone from '@/entities/venue/ui/venue-telephone';
import * as VenueTelephoneAction from '@/entities/venue/ui/venue-telephone-action';
import * as EditAccountDetailsModal from '@/features/account/ui/edit-account-details-modal';
import * as MyAccountModal from '@/features/account/ui/my-account-modal';
import * as AddEditAddressModal from '@/features/address/ui/add-edit-address-modal';
import * as AgeVerificationModal from '@/features/age-verification/ui/age-verification-modal';
import * as UnderAgeVerificationModal from '@/features/age-verification/ui/under-age-verification-modal';
import * as SetNewPasswordModal from '@/features/auth/ui/auth-modals/auth-modal-set-new-password';
import * as LoginModal from '@/features/auth/ui/auth-modals/auth-modals-login';
import * as RegisterModal from '@/features/auth/ui/auth-modals/auth-modals-register';
import * as ResetPasswordModal from '@/features/auth/ui/auth-modals/auth-modals-reset-password';
import * as VerifyPhoneNumberModal from '@/features/auth/ui/verify-phone-number-modal';
import * as BlogDetailModal from '@/features/blog/ui/blog-detail-modal';
import * as CouponModal from '@/features/coupons/ui/coupon-modal';
import * as MyCouponsModal from '@/features/coupons/ui/my-coupons-modal';
import { FavoriteItemsButton } from '@/features/favorites/ui/favorite-items-button';
import * as FavoriteItemsModal from '@/features/favorites/ui/favorite-items-modal';
import * as CookiesSettingsModal from '@/features/legal/ui/cookies-settings-modal';
import * as PrivacyPolicyModal from '@/features/legal/ui/privacy-policy-modal';
import * as RegisterTermsAndConditionsModal from '@/features/legal/ui/register-terms-and-conditions-modal';
import * as TermsAndConditionsModal from '@/features/legal/ui/terms-and-conditions-modal';
import * as NewsModal from '@/features/news/ui/news-modal';
import * as AddedToCartModal from '@/features/order/ui/added-to-cart-modal';
import * as DeliveryTrackingModal from '@/features/order/ui/driver-tracking-modal';
import * as OrderAutoFillModal from '@/features/order/ui/order-autofill-modal';
import * as OrderModal from '@/features/order/ui/order-modal';
import * as OrdersHistoryModal from '@/features/order/ui/orders-history-modal';
import * as ReviewModal from '@/features/order/ui/review-modal';
import * as TopUpOrderModal from '@/features/order/ui/top-up-order-modal';
import * as DeliveryZonesModal from '@/features/select-venue/ui/delivery-zones-modal';
import { DeliveryZonesModalLegend } from '@/features/select-venue/ui/delivery-zones-modal/delivery-zones-modal-legend';
import {
    SelectDeliveryBox,
    SelectVenue,
    SelectVenueModal,
    SelectDeliveryBoxPrefab,
    SelectVenueModalPrefab,
    SelectVenuePrefab,
    VenueRouterPagePrefab,
    registerVenueCardRenderer,
} from '@/features/venue-router';
import AppStorePrefab from '@/shared/config/prefabs/app-store.json';
import BackButtonPrefab from '@/shared/config/prefabs/back-button.json';
import BlogDetailPrefab from '@/shared/config/prefabs/blog-detail.json';
import BottomPanelPrefab from '@/shared/config/prefabs/bottom-panel.json';
import CardPrefab from '@/shared/config/prefabs/card.json';
import CarouselPrefab from '@/shared/config/prefabs/carousel.json';
import CartBonusPointsBoxPrefab from '@/shared/config/prefabs/cart-bonus-points-box.json';
import CartButtonPrefab from '@/shared/config/prefabs/cart-button.json';
import CustomerDataFormBoxPrefab from '@/shared/config/prefabs/cart-customer-data-form-box.json';
import CartDeliveryBoxPrefab from '@/shared/config/prefabs/cart-delivery-box.json';
import CartItemsListSummaryBoxPrefab from '@/shared/config/prefabs/cart-items-list-summary-box.json';
import CartPaymentMethodsBoxPrefab from '@/shared/config/prefabs/cart-payment-methods-box.json';
import CartSummaryBoxPrefab from '@/shared/config/prefabs/cart-summary-box.json';
import CartTimedOrdersBoxPrefab from '@/shared/config/prefabs/cart-timed-orders-box.json';
import CartTipBoxPrefab from '@/shared/config/prefabs/cart-tip-box.json';
import CartTopUpBoxPrefab from '@/shared/config/prefabs/cart-top-up-box.json';
import CartWrapperPrefab from '@/shared/config/prefabs/cart-wrapper.json';
import CategoriesPrefab from '@/shared/config/prefabs/categories.json';
import CategoryPrefab from '@/shared/config/prefabs/category.json';
import CenterPrefab from '@/shared/config/prefabs/center.json';
import ContactBlogBoxPrefab from '@/shared/config/prefabs/contact-blog-box.json';
import ContactDeliveryAreaBoxPrefab from '@/shared/config/prefabs/contact-delivery-area-box.json';
import ContactInfoBoxPrefab from '@/shared/config/prefabs/contact-info-box.json';
import ContactNotificationsPrefab from '@/shared/config/prefabs/contact-notifications.json';
import ContactReviewBoxPrefab from '@/shared/config/prefabs/contact-review-box.json';
import ContactSocialMediaBoxPrefab from '@/shared/config/prefabs/contact-social-media-box.json';
import CookiesButtonPrefab from '@/shared/config/prefabs/cookies-button.json';
import CookiesOverlayPrefab from '@/shared/config/prefabs/cookies-overlay.json';
import CopyrightPrefab from '@/shared/config/prefabs/copyright.json';
import CurrencySelectPrefab from '@/shared/config/prefabs/currency-select.json';
import CurrentVenueNamePrefab from '@/shared/config/prefabs/current-venue-name.json';
import CurrentVenueStatusPrefab from '@/shared/config/prefabs/current-venue-status.json';
import FavoriteItemsButtonPrefab from '@/shared/config/prefabs/favorite-items-button.json';
import FooterAppSectionPrefab from '@/shared/config/prefabs/footer-app-section.json';
import FooterSocialLinksPrefab from '@/shared/config/prefabs/footer-social-links.json';
import FooterSocialSectionPrefab from '@/shared/config/prefabs/footer-social-section.json';
import FooterPrefab from '@/shared/config/prefabs/footer.json';
import ForPrefab from '@/shared/config/prefabs/for.json';
import GooglePlayPrefab from '@/shared/config/prefabs/google-play.json';
import HeroBannerPrefab from '@/shared/config/prefabs/hero-banner.json';
import HTMLPrefab from '@/shared/config/prefabs/html.json';
import LanguageSelectPrefab from '@/shared/config/prefabs/language-select.json';
import LayoutPrefab from '@/shared/config/prefabs/layout.json';
import LinkBoxPrefab from '@/shared/config/prefabs/link-box.json';
import LinkPrefab from '@/shared/config/prefabs/link.json';
import LocalizedTextPrefab from '@/shared/config/prefabs/localized-text.json';
import LogoPrefab from '@/shared/config/prefabs/logo.json';
import MenuButtonPrefab from '@/shared/config/prefabs/menu-button.json';
import MenuCategoriesItemsPrefab from '@/shared/config/prefabs/menu-categories-items.json';
import MenuItemsPrefab from '@/shared/config/prefabs/menu-items.json';
import MiniBannersPrefab from '@/shared/config/prefabs/minibanners.json';
import ModalCloseButtonPrefab from '@/shared/config/prefabs/modal-close-button.json';
import ModalContentPrefab from '@/shared/config/prefabs/modal-content.json';
import ModalDescriptionPrefab from '@/shared/config/prefabs/modal-description.json';
import ModalFixedBottomPanelPrefab from '@/shared/config/prefabs/modal-fixed-bottom-panel.json';
import ModalFixedTopPanelPrefab from '@/shared/config/prefabs/modal-fixed-top-panel.json';
import ModalTitlePrefab from '@/shared/config/prefabs/modal-title.json';
import ModalPrefab from '@/shared/config/prefabs/modal.json';
import AddEditAddressModalPrefab from '@/shared/config/prefabs/modals/add-edit-address.json';
import AddedToCartModalPrefab from '@/shared/config/prefabs/modals/added-to-cart.json';
import AgeVerificationModalPrefab from '@/shared/config/prefabs/modals/age-verification.json';
import BlogDetailModalPrefab from '@/shared/config/prefabs/modals/blog-detail.json';
import CookiesSettingsModalPrefab from '@/shared/config/prefabs/modals/cookies-settings.json';
import CouponModalPrefab from '@/shared/config/prefabs/modals/coupon.json';
import DeliveryZonesModalPrefab from '@/shared/config/prefabs/modals/delivery-zones.json';
import DeliveryTrackingModalPrefab from '@/shared/config/prefabs/modals/driver-tracking.json';
import EditAccountDetailsModalPrefab from '@/shared/config/prefabs/modals/edit-account-details.json';
import FavoriteItemsModalPrefab from '@/shared/config/prefabs/modals/favorite-items.json';
import LoginModalPrefab from '@/shared/config/prefabs/modals/login.json';
import MyAccountModalPrefab from '@/shared/config/prefabs/modals/my-account.json';
import MyCouponsModalPrefab from '@/shared/config/prefabs/modals/my-coupons.json';
import NewsDetailPrefab from '@/shared/config/prefabs/modals/news-detail.json';
import NewsNewPrefab from '@/shared/config/prefabs/modals/news-new.json';
import OrderAutoFillModalPrefab from '@/shared/config/prefabs/modals/order-autofill.json';
import OrderModalPrefab from '@/shared/config/prefabs/modals/order.json';
import OrdersHistoryModalPrefab from '@/shared/config/prefabs/modals/orders-history.json';
import PrivacyPolicyModalPrefab from '@/shared/config/prefabs/modals/privacy-policy.json';
import ProductDetailModalPrefab from '@/shared/config/prefabs/modals/product-detail-modal.json';
import RegisterTermsAndConditionsModalPrefab from '@/shared/config/prefabs/modals/register-terms-and-conditions.json';
import RegisterModalPrefab from '@/shared/config/prefabs/modals/register.json';
import ResetPasswordModalPrefab from '@/shared/config/prefabs/modals/reset-password.json';
import ReviewModalPrefab from '@/shared/config/prefabs/modals/review.json';
import SetNewPasswordModalPrefab from '@/shared/config/prefabs/modals/set-new-password.json';
import TermsAndConditionsModalPrefab from '@/shared/config/prefabs/modals/terms-and-conditions.json';
import TopUpOrderModalPrefab from '@/shared/config/prefabs/modals/top-up-order.json';
import UnderAgeVerificationModalPrefab from '@/shared/config/prefabs/modals/under-age-verification.json';
import VerifyPhoneNumberModalPrefab from '@/shared/config/prefabs/modals/verify-phone-number.json';
import NavbarPrefab from '@/shared/config/prefabs/navbar.json';
import OrderLandingPrefab from '@/shared/config/prefabs/order-landing.json';
import OrderMageBrandingPrefab from '@/shared/config/prefabs/ordermage-branding.json';
import OutagesPrefab from '@/shared/config/prefabs/outages.json';
import PageLayoutPrefab from '@/shared/config/prefabs/page-layout.json';
import PageAlergensPrefab from '@/shared/config/prefabs/pages/alergens.json';
import PageBlog1Prefab from '@/shared/config/prefabs/pages/blog-1.json';
import PageCartPrefab from '@/shared/config/prefabs/pages/cart.json';
import PageContactPrefab from '@/shared/config/prefabs/pages/contact.json';
import PageHomePrefab from '@/shared/config/prefabs/pages/home.json';
import PageOrderPrefab from '@/shared/config/prefabs/pages/order.json';
import PageProductDetailPrefab from '@/shared/config/prefabs/pages/product-detail.json';
import PrivacyPolicyButtonPrefab from '@/shared/config/prefabs/privacy-policy-button.json';
import ProductCardPrefab from '@/shared/config/prefabs/product-card.json';
import ProductDetailDetailsPrefab from '@/shared/config/prefabs/product-detail-details.json';
import ProductDetailGalleryCarouselPrefab from '@/shared/config/prefabs/product-detail-gallery-carousel.json';
import ProductDetailGalleryMainImagePrefab from '@/shared/config/prefabs/product-detail-gallery-main-image.json';
import ProductDetailGalleryPrefab from '@/shared/config/prefabs/product-detail-gallery.json';
import ProductDetailInfoAddToCartPrefab from '@/shared/config/prefabs/product-detail-info-add-to-cart.json';
import ProductDetailInfoAlergensPrefab from '@/shared/config/prefabs/product-detail-info-alergens.json';
import ProductDetailInfoCouponPrefab from '@/shared/config/prefabs/product-detail-info-coupon.json';
import ProductDetailInfoDescriptionPrefab from '@/shared/config/prefabs/product-detail-info-description.json';
import ProductDetailInfoEnergyValuesTablePrefab from '@/shared/config/prefabs/product-detail-info-energy-values-table.json';
import ProductDetailInfoGalleryActionsFallbackPrefab from '@/shared/config/prefabs/product-detail-info-gallery-actions-fallback.json';
import ProductDetailInfoNamePrefab from '@/shared/config/prefabs/product-detail-info-name.json';
import ProductDetailInfoPackagingPrefab from '@/shared/config/prefabs/product-detail-info-packaging.json';
import ProductDetailInfoPricePrefab from '@/shared/config/prefabs/product-detail-info-price.json';
import ProductDetailInfoShowMorePrefab from '@/shared/config/prefabs/product-detail-info-show-more.json';
import ProductDetailInfoVariantSelectPrefab from '@/shared/config/prefabs/product-detail-info-variant-select.json';
import ProductDetailInfoPrefab from '@/shared/config/prefabs/product-detail-info.json';
import ProductDetailTagsPrefab from '@/shared/config/prefabs/product-detail-tags.json';
import ProductDetailPrefab from '@/shared/config/prefabs/product-detail.json';
import ProductMenuNotePrefab from '@/shared/config/prefabs/product-menu-note.json';
import ScrollButtonPrefab from '@/shared/config/prefabs/scroll-button.json';
import SeparatorPrefab from '@/shared/config/prefabs/separator.json';
import SlotPrefab from '@/shared/config/prefabs/slot.json';
import StickyBoxPrefab from '@/shared/config/prefabs/sticky-box.json';
import SubcategoryPrefab from '@/shared/config/prefabs/subcategory.json';
import TagsPrefab from '@/shared/config/prefabs/tags.json';
import TermsAndConditionsButtonPrefab from '@/shared/config/prefabs/terms-and-conditions-button.json';
import TrackedOrdersButtonPrefab from '@/shared/config/prefabs/tracked-orders-button.json';
import UserButtonPrefab from '@/shared/config/prefabs/user-button.json';
import VenueAddressActionPrefab from '@/shared/config/prefabs/venue-address-action.json';
import VenueAddressPrefab from '@/shared/config/prefabs/venue-address.json';
import VenueCardPrefab from '@/shared/config/prefabs/venue-card.json';
import VenueListPrefab from '@/shared/config/prefabs/venue-list.json';
import VenueRouterHeroPrefab from '@/shared/config/prefabs/venue-router-hero.json';
import VenueTelephoneActionPrefab from '@/shared/config/prefabs/venue-telephone-action.json';
import VenueTelephonePrefab from '@/shared/config/prefabs/venue-telephone.json';
import { AppStore } from '@/shared/ui/app-store';
import { BackButton } from '@/shared/ui/back-button';
import { Card } from '@/shared/ui/card';
import { Center } from '@/shared/ui/center';
import { CookiesButton } from '@/shared/ui/cookies-button';
import * as CookiesOverlay from '@/shared/ui/cookies-overlay';
import { Copyright } from '@/shared/ui/copyright';
import { CurrencySelect } from '@/shared/ui/currency-select';
import { For } from '@/shared/ui/for';
import { VirtualizedMenuCategoriesFeed } from '@/shared/ui/for/virtualized-menu-categories-feed';
import { GooglePlay } from '@/shared/ui/google-play';
import { HTML } from '@/shared/ui/html';
import { LanguageSelect } from '@/shared/ui/language-select';
import { Layout } from '@/shared/ui/layout';
import { Link } from '@/shared/ui/link';
import { LinkBox } from '@/shared/ui/link-box';
import { LocalizedText } from '@/shared/ui/localized-text';
import { Logo } from '@/shared/ui/logo';
import { Modal } from '@/shared/ui/modal';
import { OrderMageBranding } from '@/shared/ui/ordermage-branding';
import { PrivacyPolicyButton } from '@/shared/ui/privacy-policy-button';
import { ScrollButton } from '@/shared/ui/scroll-button';
import { Separator } from '@/shared/ui/separator';
import { StickyBox } from '@/shared/ui/sticky-box';
import { Tags } from '@/shared/ui/tags';
import { TermsAndConditionsButton } from '@/shared/ui/terms-and-conditions-button';
import { TrackedOrdersButton } from '@/shared/ui/tracked-orders-button';
import { UserButton } from '@/shared/ui/user-button';
import { BlogDetailParts as BlogDetail } from '@/widgets/blog';
import { BottomPanelCartTab } from '@/widgets/bottom-panel/bottom-panel-cart-tab';
import { BottomPanelFavoriteItemsTab } from '@/widgets/bottom-panel/bottom-panel-favorite-items';
import { BottomPanelHomeTab } from '@/widgets/bottom-panel/bottom-panel-home-tab';
import {
    Cart,
    CartBonusPointsPaymentBox,
    CartButton,
    CartCustomerDataFormBox,
    CartDeliveryBox,
    CartItemsListSummaryBox,
    CartPaymentMethodsBox,
    CartSummaryBox,
    CartTimedOrdersBox,
    CartTipBox,
    CartTopUpBox,
} from '@/widgets/cart';
import * as ContactBlogBox from '@/widgets/contact/contact-blog-box';
import * as ContactDeliveryAreaBox from '@/widgets/contact/contact-delivery-area-box';
import * as ContactInfoBox from '@/widgets/contact/contact-info-box';
import * as ContactReviewBox from '@/widgets/contact/contact-review-box';
import * as ContactSocialMediaBox from '@/widgets/contact/contact-social-media-box';
import { FooterAppLinks, FooterAppSection } from '@/widgets/footer/footer-app-section';
import { FooterSocialLinks } from '@/widgets/footer/footer-social-links';
import { FooterSocialSection } from '@/widgets/footer/footer-social-section';
import { HeroBanner } from '@/widgets/hero-banner';
import { MenuButton } from '@/widgets/menu/menu-button';
import { MiniBanners } from '@/widgets/mini-banners';
import { OutagesWrapper, OutagesIcon, OutagesMessage } from '@/widgets/outages';
import { PrefabErrorBoundary } from './prefab-error-boundary';

export const PrefabRegistry = {
    StickyBox: StickyBoxPrefab,
    BottomPanel: BottomPanelPrefab,
    Card: CardPrefab,
    Carousel: CarouselPrefab,
    Categories: CategoriesPrefab,
    Category: CategoryPrefab,
    VenueCard: VenueCardPrefab,
    VenueList: VenueListPrefab,
    Subcategory: SubcategoryPrefab,
    ScrollButton: ScrollButtonPrefab,
    Layout: LayoutPrefab,
    Center: CenterPrefab,
    For: ForPrefab,
    Footer: FooterPrefab,
    FooterAppSection: FooterAppSectionPrefab,
    FooterSocialLinks: FooterSocialLinksPrefab,
    FooterSocialSection: FooterSocialSectionPrefab,
    Logo: LogoPrefab,
    CurrentVenueStatus: CurrentVenueStatusPrefab,
    VenueTelephone: VenueTelephonePrefab,
    VenueAddress: VenueAddressPrefab,
    CurrencySelect: CurrencySelectPrefab,
    LanguageSelect: LanguageSelectPrefab,
    Separator: SeparatorPrefab,
    Slot: SlotPrefab,
    Copyright: CopyrightPrefab,
    TermsAndConditionsButton: TermsAndConditionsButtonPrefab,
    PrivacyPolicyButton: PrivacyPolicyButtonPrefab,
    CookiesButton: CookiesButtonPrefab,
    CookiesOverlay: CookiesOverlayPrefab,
    BlogDetail: BlogDetailPrefab,
    LocalizedText: LocalizedTextPrefab,
    Link: LinkPrefab,
    Modal: ModalPrefab,
    'Modal.Title': ModalTitlePrefab,
    'Modal.Description': ModalDescriptionPrefab,
    'Modal.FixedTopPanel': ModalFixedTopPanelPrefab,
    'Modal.FixedBottomPanel': ModalFixedBottomPanelPrefab,
    'Modal.CloseButton': ModalCloseButtonPrefab,
    'Modal.Content': ModalContentPrefab,
    AppStore: AppStorePrefab,
    GooglePlay: GooglePlayPrefab,
    UserButton: UserButtonPrefab,
    MenuButton: MenuButtonPrefab,
    CartButton: CartButtonPrefab,
    TrackedOrdersButton: TrackedOrdersButtonPrefab,
    Navbar: NavbarPrefab,
    OrderMageBranding: OrderMageBrandingPrefab,
    LinkBox: LinkBoxPrefab,
    'Cart.Wrapper': CartWrapperPrefab,
    'Order.Landing': OrderLandingPrefab,
    'Cart.BonusPointsBox': CartBonusPointsBoxPrefab,
    'Cart.DeliveryBox': CartDeliveryBoxPrefab,
    'Cart.ItemsListSummaryBox': CartItemsListSummaryBoxPrefab,
    'Cart.PaymentMethodsBox': CartPaymentMethodsBoxPrefab,
    'Cart.SummaryBox': CartSummaryBoxPrefab,
    'Cart.TimedOrdersBox': CartTimedOrdersBoxPrefab,
    'Cart.TipBox': CartTipBoxPrefab,
    'Cart.TopUpBox': CartTopUpBoxPrefab,
    'Cart.CustomerDataFormBox': CustomerDataFormBoxPrefab,
    PageLayout: PageLayoutPrefab,
    HeroBanner: HeroBannerPrefab,
    MiniBanners: MiniBannersPrefab,
    MenuCategoriesItems: MenuCategoriesItemsPrefab,
    MenuItems: MenuItemsPrefab,
    ProductCard: ProductCardPrefab,
    ProductDetail: ProductDetailPrefab,
    ProductDetailModal: ProductDetailModalPrefab,
    'ProductDetail.Gallery': ProductDetailGalleryPrefab,
    'ProductDetail.Gallery.MainImage': ProductDetailGalleryMainImagePrefab,
    'ProductDetail.Gallery.Carousel': ProductDetailGalleryCarouselPrefab,
    'ProductDetail.Info': ProductDetailInfoPrefab,
    'ProductDetail.Info.Name': ProductDetailInfoNamePrefab,
    'ProductDetail.Info.Description': ProductDetailInfoDescriptionPrefab,
    'ProductDetail.Info.Coupon': ProductDetailInfoCouponPrefab,
    'ProductDetail.Info.Price': ProductDetailInfoPricePrefab,
    'ProductDetail.Info.Packaging': ProductDetailInfoPackagingPrefab,
    'ProductDetail.Info.VariantSelect': ProductDetailInfoVariantSelectPrefab,
    'ProductDetail.Info.AddToCart': ProductDetailInfoAddToCartPrefab,
    'ProductDetail.Info.Alergens': ProductDetailInfoAlergensPrefab,
    'ProductDetail.Info.EnergyValuesTable': ProductDetailInfoEnergyValuesTablePrefab,
    'ProductDetail.Info.GalleryActionsFallback': ProductDetailInfoGalleryActionsFallbackPrefab,
    'ProductDetail.Info.ShowMore': ProductDetailInfoShowMorePrefab,
    'ProductDetail.Info.Tags': ProductDetailTagsPrefab,
    'ProductDetail.Details': ProductDetailDetailsPrefab,
    ProductMenuNote: ProductMenuNotePrefab,
    Tags: TagsPrefab,
    ContactBlogBox: ContactBlogBoxPrefab,
    ContactInfoBox: ContactInfoBoxPrefab,
    ContactNotifications: ContactNotificationsPrefab,
    ContactDeliveryAreaBox: ContactDeliveryAreaBoxPrefab,
    ContactReviewBox: ContactReviewBoxPrefab,
    ContactSocialMediaBox: ContactSocialMediaBoxPrefab,
    VenueTelephoneAction: VenueTelephoneActionPrefab,
    VenueAddressAction: VenueAddressActionPrefab,
    CurrentVenueName: CurrentVenueNamePrefab,
    FavoriteItemsButton: FavoriteItemsButtonPrefab,
    HTML: HTMLPrefab,
    Outages: OutagesPrefab,
    AddedToCartModal: AddedToCartModalPrefab,
    AddEditAddressModal: AddEditAddressModalPrefab,
    AgeVerificationModal: AgeVerificationModalPrefab,
    UnderAgeVerificationModal: UnderAgeVerificationModalPrefab,
    BlogDetailModal: BlogDetailModalPrefab,
    CookiesSettingsModal: CookiesSettingsModalPrefab,
    CouponModal: CouponModalPrefab,
    DeliveryZonesModal: DeliveryZonesModalPrefab,
    DeliveryTrackingModal: DeliveryTrackingModalPrefab,
    EditAccountDetailsModal: EditAccountDetailsModalPrefab,
    FavoriteItemsModal: FavoriteItemsModalPrefab,
    LoginModal: LoginModalPrefab,
    MyAccountModal: MyAccountModalPrefab,
    MyCouponsModal: MyCouponsModalPrefab,
    NewsDetail: NewsDetailPrefab,
    NewsNew: NewsNewPrefab,
    OrderAutoFillModal: OrderAutoFillModalPrefab,
    OrderModal: OrderModalPrefab,
    OrdersHistoryModal: OrdersHistoryModalPrefab,
    PrivacyPolicyModal: PrivacyPolicyModalPrefab,
    RegisterModal: RegisterModalPrefab,
    RegisterTermsAndConditionsModal: RegisterTermsAndConditionsModalPrefab,
    ResetPasswordModal: ResetPasswordModalPrefab,
    ReviewModal: ReviewModalPrefab,
    SelectVenue: SelectVenuePrefab,
    SelectDeliveryBox: SelectDeliveryBoxPrefab,
    VenueRouterHero: VenueRouterHeroPrefab,
    SelectVenueModal: SelectVenueModalPrefab,
    SetNewPasswordModal: SetNewPasswordModalPrefab,
    TermsAndConditionsModal: TermsAndConditionsModalPrefab,
    TopUpOrderModal: TopUpOrderModalPrefab,
    VerifyPhoneNumberModal: VerifyPhoneNumberModalPrefab,
    BackButton: BackButtonPrefab,
    'Page.Home': PageHomePrefab,
    'Page.ProductDetail': PageProductDetailPrefab,
    'Page.Cart': PageCartPrefab,
    'Page.Order': PageOrderPrefab,
    'Page.Alergens': PageAlergensPrefab,
    'Page.Contact': PageContactPrefab,
    'Page.Blog1': PageBlog1Prefab,
    'Page.VenueRouter': VenueRouterPagePrefab,
};

const ComponentRegistry = {
    StickyBox,
    Layout,
    For,
    VirtualizedMenuCategoriesFeed,
    'Categories.Wrapper': Categories.Wrapper,
    'Categories.Carousel': Categories.Carousel,
    'Categories.CarouselWrapper': Categories.CarouselWrapper,
    'Categories.ScrollIndicators': Categories.ScrollIndicators,
    'Categories.ScrollIndicator': Categories.ScrollIndicator,
    'Category.Wrapper': Categories.CategoryWrapper,
    'Category.Icon': Categories.CategoryIcon,
    'Category.Name': Categories.CategoryName,
    'Category.Description': Categories.CategoryDescription,
    'SubCategory.Wrapper': Categories.SubCategoryWrapper,
    'SubCategory.Icon': Categories.SubCategoryIcon,
    'SubCategory.Name': Categories.SubCategoryName,
    ScrollButton,
    Logo,
    Carousel: RecommendedCarousel,
    Center,
    CurrentVenueStatus,
    'VenueTelephone.Wrapper': VenueTelephone.Wrapper,
    'VenueTelephone.Text': VenueTelephone.Text,
    'VenueTelephone.Icon': VenueTelephone.Icon,
    'VenueAddress.Wrapper': VenueAddress.Wrapper,
    'VenueAddress.Text': VenueAddress.Text,
    'VenueAddress.Icon': VenueAddress.Icon,
    'Venue.Wrapper': Venue.Wrapper,
    'Venue.Image': Venue.Image,
    'Venue.Tags': Venue.Tags,
    'Venue.Logo': Venue.Logo,
    'Venue.Title': Venue.Title,
    'Venue.Status': Venue.Status,
    'Venue.Address': Venue.Address,
    'Venue.Telephone': Venue.Telephone,
    'Venue.Email': Venue.Email,
    'Venue.Chevron': Venue.Chevron,
    'Venue.Distance': Venue.Distance,
    CurrencySelect,
    LanguageSelect,
    Separator,
    Copyright,
    TermsAndConditionsButton,
    PrivacyPolicyButton,
    CookiesButton,
    'CookiesOverlay.Wrapper': CookiesOverlay.CookiesOverlayWrapper,
    'CookiesOverlay.Text': CookiesOverlay.CookiesOverlayText,
    'CookiesOverlay.Bold': CookiesOverlay.CookiesOverlayBold,
    'CookiesOverlay.Link': CookiesOverlay.CookiesOverlayLink,
    'CookiesOverlay.AllowAllButton': CookiesOverlay.CookiesOverlayAllowAllButton,
    'CookiesOverlay.AllowAllButton.Icon': CookiesOverlay.CookiesOverlayAllowAllButtonIcon,
    'CookiesOverlay.AllowAllButton.Text': CookiesOverlay.CookiesOverlayAllowAllButtonText,
    'CookiesOverlay.SettingsButton': CookiesOverlay.CookiesOverlaySettingsButton,
    'BlogDetail.Wrapper': BlogDetail.BlogDetailWrapper,
    'BlogDetail.Title': BlogDetail.BlogDetailTitle,
    'BlogDetail.Tags': BlogDetail.BlogDetailTags,
    'BlogDetail.Description': BlogDetail.BlogDetailDescription,
    'BlogDetail.Content': BlogDetail.BlogDetailContent,
    'BlogDetail.Media': BlogDetail.BlogDetailMedia,
    LocalizedText,
    Link,
    AppStore,
    FooterAppLinks,
    FooterAppSection,
    FooterSocialLinks,
    FooterSocialSection,
    GooglePlay,
    UserButton,
    MenuButton,
    'ProductCard.Wrapper': ProductCard.Wrapper,
    'ProductCard.Image': ProductCard.ProductCardImage,
    'ProductCard.ContentWrapper': ProductCard.ProductCardContentWrapper,
    'ProductCard.BottomOverlayWrapper': ProductCard.ProductCardBottomOverlayWrapper,
    'ProductCard.Availability': ProductCard.ProductCardAvailability,
    'ProductCard.Description': ProductCard.ProductCardDescription,
    'ProductCard.Name': ProductCard.ProductCardName,
    'ProductCard.VariantSelect': ProductCard.ProductCardSelect,
    'ProductCard.Tags': ProductCard.ProductCardTags,
    'ProductCard.Price': ProductCard.ProductCardPrice,
    'ProductCard.OriginalPrice': ProductCard.ProductCardOriginalPrice,
    'ProductCard.AddToCartButton': ProductCard.ProductCardAddToCartButton,
    'ProductCard.AddToCartButton.Icon': ProductCard.ProductCardAddToCartButtonIcon,
    'ProductCard.AddToCartButton.Text': ProductCard.ProductCardAddToCartButtonText,
    ProductDetail: ProductDetailComponent,
    'ProductDetailModal.Wrapper': ProductDetailModal.Wrapper,
    'ProductDetailModal.Provider': ProductDetailModal.Provider,
    'ProductDetailModal.Image': ProductDetailModal.Image,
    'ProductDetailModal.Title': ProductDetailModal.Title,
    'ProductDetailModal.Description': ProductDetailModal.Description,
    'ProductDetailModal.AddonsList': ProductDetailModal.AddonsList,
    'ProductDetailModal.QuantityInput': ProductDetailModal.QuantityInput,
    'ProductDetailModal.AddToCartButton': ProductDetailModal.AddToCartButton,
    'ProductDetailModal.Footer': ProductDetailModal.Footer,
    'ProductDetail.Gallery': ProductDetailParts.Gallery,
    'ProductDetail.Gallery.MainImage': ProductDetailParts.Gallery.MainImage,
    'ProductDetail.Gallery.Carousel': ProductDetailParts.Gallery.Carousel,
    'ProductDetail.Info': ProductDetailParts.Info,
    'ProductDetail.Info.Name': ProductDetailParts.Info.Name,
    'ProductDetail.Info.Description': ProductDetailParts.Info.Description,
    'ProductDetail.Info.Coupon': ProductDetailParts.Info.Coupon,
    'ProductDetail.Info.Price': ProductDetailParts.Info.Price,
    'ProductDetail.Info.Packaging': ProductDetailParts.Info.Packaging,
    'ProductDetail.Info.VariantSelect': ProductDetailParts.Info.VariantSelect,
    'ProductDetail.Info.AddToCart': ProductDetailParts.Info.AddToCart,
    'ProductDetail.Info.Alergens': ProductDetailParts.Info.Alergens,
    'ProductDetail.Info.EnergyValuesTable': ProductDetailParts.Info.EnergyValuesTable,
    'ProductDetail.Info.GalleryActionsFallback': ProductDetailParts.Gallery.ActionsInfoFallback,
    'ProductDetail.Info.ShowMore': ProductDetailParts.Info.ShowMore,
    'ProductDetail.Info.Tags': ProductDetailParts.Info.Tags,
    'ProductDetail.Details': ProductDetailParts.Details,
    ProductMenuNote,
    Tags,
    'ContactBlogBox.Wrapper': ContactBlogBox.ContactBlogBoxWrapper,
    'ContactBlogBox.NotSeenAvatar': ContactBlogBox.ContactBlogBoxNotSeenAvatar,
    'ContactBlogBox.Media': ContactBlogBox.ContactBlogBoxMedia,
    'ContactBlogBox.ContentWrapper': ContactBlogBox.ContactBlogBoxContentWrapper,
    'ContactBlogBox.Content': ContactBlogBox.ContactBlogBoxContent,
    'ContactBlogBox.Title': ContactBlogBox.ContactBlogBoxTitle,
    'ContactBlogBox.Description': ContactBlogBox.ContactBlogBoxDescription,
    'ContactBlogBox.PublishedAt': ContactBlogBox.ContactBlogBoxPublishedAt,
    'ContactBlogBox.ChevronIcon': ContactBlogBox.ContactBlogBoxChevronIcon,
    'ContactInfoBox.Wrapper': ContactInfoBox.ContactInfoBoxWrapper,
    'ContactInfoBox.Title': ContactInfoBox.ContactInfoBoxTitle,
    'ContactInfoBox.Actions': ContactInfoBox.ContactInfoBoxActions,
    'ContactInfoBox.Divider': ContactInfoBox.ContactInfoBoxDivider,
    'ContactInfoBox.SocialTitle': ContactInfoBox.ContactInfoBoxSocialTitle,
    'ContactInfoBox.SocialLinks': ContactInfoBox.ContactInfoBoxSocialLinks,
    'VenueTelephoneAction.Wrapper': VenueTelephoneAction.VenueTelephoneActionWrapper,
    'VenueTelephoneAction.Icon': VenueTelephoneAction.VenueTelephoneActionIcon,
    'VenueTelephoneAction.Text': VenueTelephoneAction.VenueTelephoneActionText,
    'VenueTelephoneAction.Button': VenueTelephoneAction.VenueTelephoneActionButton,
    'VenueAddressAction.Wrapper': VenueAddressAction.VenueAddressActionWrapper,
    'VenueAddressAction.Icon': VenueAddressAction.VenueAddressActionIcon,
    'VenueAddressAction.Text': VenueAddressAction.VenueAddressActionText,
    'VenueAddressAction.Button': VenueAddressAction.VenueAddressActionButton,
    'ContactDeliveryAreaBox.Wrapper': ContactDeliveryAreaBox.ContactDeliveryAreaBoxWrapper,
    'ContactDeliveryAreaBox.Title': ContactDeliveryAreaBox.ContactDeliveryAreaBoxTitle,
    'ContactDeliveryAreaBox.Button': ContactDeliveryAreaBox.ContactDeliveryAreaBoxButton,
    'ContactDeliveryAreaBox.MapWrapper': ContactDeliveryAreaBox.ContactDeliveryAreaBoxMapWrapper,
    'ContactDeliveryAreaBox.MapOverlay': ContactDeliveryAreaBox.ContactDeliveryAreaBoxMapOverlay,
    'ContactDeliveryAreaBox.Map': ContactDeliveryAreaBox.ContactDeliveryAreaBoxMap,
    'ContactReviewBox.Wrapper': ContactReviewBox.ContactReviewBoxWrapper,
    'ContactReviewBox.Title': ContactReviewBox.ContactReviewBoxTitle,
    'ContactReviewBox.Divider': ContactReviewBox.ContactReviewBoxDivider,
    'ContactReviewBox.Platforms': ContactReviewBox.ContactReviewBoxPlatforms,
    'ContactSocialMediaBox.Wrapper': ContactSocialMediaBox.ContactSocialMediaBoxWrapper,
    'ContactSocialMediaBox.Media': ContactSocialMediaBox.ContactSocialMediaBoxMedia,
    'ContactSocialMediaBox.NotSeenAvatar': ContactSocialMediaBox.ContactSocialMediaBoxNotSeenAvatar,
    'ContactSocialMediaBox.Platform': ContactSocialMediaBox.ContactSocialMediaBoxPlatform,
    'MenuItem.Card': MenuItemCard,
    CartButton,
    TrackedOrdersButton,
    LinkBox,
    'Cart.Wrapper': Cart,
    'Order.Landing': dynamic(
        () => import('@/features/order/ui/order-landing').then((mod) => mod.OrderLanding),
        { ssr: false },
    ),
    'Cart.BonusPointsBox': CartBonusPointsPaymentBox,
    'Cart.DeliveryBox': CartDeliveryBox,
    'Cart.ItemsListSummaryBox': CartItemsListSummaryBox,
    'Cart.PaymentMethodsBox': CartPaymentMethodsBox,
    'Cart.SummaryBox': CartSummaryBox,
    'Cart.TimedOrdersBox': CartTimedOrdersBox,
    'Cart.TipBox': CartTipBox,
    'Cart.TopUpBox': CartTopUpBox,
    'Cart.CustomerDataFormBox': CartCustomerDataFormBox,
    PageLayout,
    HeroBanner,
    MiniBanners,
    Card,
    SelectVenue,
    SelectDeliveryBox,
    CurrentVenueName,
    FavoriteItemsButton,
    'BottomPanel.Tab.Home': BottomPanelHomeTab,
    'BottomPanel.Tab.Cart': BottomPanelCartTab,
    'BottomPanel.Tab.FavoriteItems': BottomPanelFavoriteItemsTab,
    HTML,
    Alergens,
    OrderMageBranding,
    DeliveryAreasMap: dynamic(
        () => import('@/features/select-venue/ui/delivery-area-map').then((mod) => mod.DeliveryAreasMap),
        { ssr: false },
    ),
    'DeliveryZonesModal.Legend': DeliveryZonesModalLegend,
    'Outages.Wrapper': OutagesWrapper,
    'Outages.Icon': OutagesIcon,
    'Outages.Message': OutagesMessage,
    Modal,
    'Modal.Title': Modal.Title,
    'Modal.Description': Modal.Description,
    'Modal.CloseButton': Modal.CloseButton,
    'Modal.Content': Modal.Content,
    'Modal.FixedTopPanel': Modal.FixedTopPanel,
    'Modal.FixedBottomPanel': Modal.FixedBottomPanel,
    'AddedToCartModal.Wrapper': AddedToCartModal.Wrapper,
    'AddedToCartModal.Provider': AddedToCartModal.Provider,
    'AddedToCartModal.TopPanel': AddedToCartModal.TopPanel,
    'AddedToCartModal.Title': AddedToCartModal.Title,
    'AddedToCartModal.Image': AddedToCartModal.Image,
    'AddedToCartModal.Name': AddedToCartModal.Name,
    'AddedToCartModal.Variant': AddedToCartModal.Variant,
    'AddedToCartModal.AddedModifications': AddedToCartModal.AddedModifications,
    'AddedToCartModal.RemovedModifications': AddedToCartModal.RemovedModifications,
    'AddedToCartModal.Modifications': AddedToCartModal.Modifications,
    'AddedToCartModal.BackButton': AddedToCartModal.BackButton,
    'AddedToCartModal.GoToCartButton': AddedToCartModal.GoToCartButton,
    'AddEditAddressModal.Wrapper': AddEditAddressModal.Wrapper,
    'AgeVerificationModal.Wrapper': AgeVerificationModal.Wrapper,
    'AgeVerificationModal.Provider': AgeVerificationModal.Provider,
    'AgeVerificationModal.Title': AgeVerificationModal.Title,
    'AgeVerificationModal.Description': AgeVerificationModal.Description,
    'AgeVerificationModal.NoButton': AgeVerificationModal.NoButton,
    'AgeVerificationModal.YesButton': AgeVerificationModal.YesButton,
    'UnderAgeVerificationModal.Wrapper': UnderAgeVerificationModal.Wrapper,
    'UnderAgeVerificationModal.Provider': UnderAgeVerificationModal.Provider,
    'UnderAgeVerificationModal.Title': UnderAgeVerificationModal.Title,
    'UnderAgeVerificationModal.Description': UnderAgeVerificationModal.Description,
    'UnderAgeVerificationModal.OkayButton': UnderAgeVerificationModal.OkayButton,
    'OrderAutoFillModal.Wrapper': OrderAutoFillModal.Wrapper,
    'OrderAutoFillModal.Provider': OrderAutoFillModal.Provider,
    'OrderAutoFillModal.Title': OrderAutoFillModal.Title,
    'OrderAutoFillModal.Description': OrderAutoFillModal.Description,
    'OrderAutoFillModal.NoButton': OrderAutoFillModal.NoButton,
    'OrderAutoFillModal.YesButton': OrderAutoFillModal.YesButton,
    'BlogDetailModal.Wrapper': BlogDetailModal.Wrapper,
    'BlogDetailModal.Provider': BlogDetailModal.Provider,
    'BlogDetailModal.Title': BlogDetailModal.Title,
    'BlogDetailModal.BlogDetail': BlogDetailModal.BlogDetailComponent,
    'CookiesSettingsModal.Wrapper': CookiesSettingsModal.Wrapper,
    'CookiesSettingsModal.Provider': CookiesSettingsModal.Provider,
    'CookiesSettingsModal.Form': CookiesSettingsModal.Form,
    'CookiesSettingsModal.Title': CookiesSettingsModal.Title,
    'CookiesSettingsModal.RecommendedTitle': CookiesSettingsModal.RecommendedTitle,
    'CookiesSettingsModal.RecommendedDescription': CookiesSettingsModal.RecommendedDescription,
    'CookiesSettingsModal.TechnicalTitle': CookiesSettingsModal.TechnicalTitle,
    'CookiesSettingsModal.TechnicalDescription': CookiesSettingsModal.TechnicalDescription,
    'CookiesSettingsModal.AnalyticalTitle': CookiesSettingsModal.AnalyticalTitle,
    'CookiesSettingsModal.AnalyticalDescription': CookiesSettingsModal.AnalyticalDescription,
    'CookiesSettingsModal.MarketingTitle': CookiesSettingsModal.MarketingTitle,
    'CookiesSettingsModal.MarketingDescription': CookiesSettingsModal.MarketingDescription,
    'CookiesSettingsModal.RecommendedSwitch': CookiesSettingsModal.RecommendedSwitch,
    'CookiesSettingsModal.TechnicalSwitch': CookiesSettingsModal.TechnicalSwitch,
    'CookiesSettingsModal.AnalyticalSwitch': CookiesSettingsModal.AnalyticalSwitch,
    'CookiesSettingsModal.MarketingSwitch': CookiesSettingsModal.MarketingSwitch,
    'CookiesSettingsModal.SubmitButton': CookiesSettingsModal.SubmitButton,
    'PrivacyPolicyModal.Wrapper': PrivacyPolicyModal.Wrapper,
    'PrivacyPolicyModal.Provider': PrivacyPolicyModal.Provider,
    'PrivacyPolicyModal.Title': PrivacyPolicyModal.Title,
    'PrivacyPolicyModal.Content': PrivacyPolicyModal.Content,
    'PrivacyPolicyModal.AcceptButton': PrivacyPolicyModal.AcceptButton,
    'TermsAndConditionsModal.Wrapper': TermsAndConditionsModal.Wrapper,
    'TermsAndConditionsModal.Provider': TermsAndConditionsModal.Provider,
    'TermsAndConditionsModal.Title': TermsAndConditionsModal.Title,
    'TermsAndConditionsModal.Content': TermsAndConditionsModal.Content,
    'TermsAndConditionsModal.AcceptButton': TermsAndConditionsModal.AcceptButton,
    'RegisterTermsAndConditionsModal.Wrapper': RegisterTermsAndConditionsModal.Wrapper,
    'RegisterTermsAndConditionsModal.Provider': RegisterTermsAndConditionsModal.Provider,
    'RegisterTermsAndConditionsModal.Title': RegisterTermsAndConditionsModal.Title,
    'RegisterTermsAndConditionsModal.Content': RegisterTermsAndConditionsModal.Content,
    'RegisterTermsAndConditionsModal.AcceptButton': RegisterTermsAndConditionsModal.AcceptButton,
    'CouponModal.Wrapper': CouponModal.Wrapper,
    'CouponModal.Provider': CouponModal.Provider,
    'CouponModal.Title': CouponModal.Title,
    'CouponModal.Image': CouponModal.Image,
    'CouponModal.Name': CouponModal.Name,
    'CouponModal.QRPlaceholder': CouponModal.QRPlaceholder,
    'CouponModal.ApplyButton': CouponModal.ApplyButton,
    'CouponModal.DownloadButton': CouponModal.DownloadButton,
    'CouponModal.DetailsTable': CouponModal.DetailsTable,
    'DeliveryZonesModal.Wrapper': DeliveryZonesModal.Wrapper,
    'DeliveryZonesModal.Provider': DeliveryZonesModal.Provider,
    'DeliveryZonesModal.Title': DeliveryZonesModal.Title,
    'DeliveryTrackingModal.Wrapper': DeliveryTrackingModal.Wrapper,
    'DeliveryTrackingModal.Provider': DeliveryTrackingModal.Provider,
    'DeliveryTrackingModal.Map': DeliveryTrackingModal.Map,
    'DeliveryTrackingModal.DriverDetails': dynamic(
        () =>
            import('@/features/order/ui/driver-tracking-modal/delivery-tracking-modal-driver-details').then(
                (mod) => mod.DeliveryTrackingModalDriverDetails,
            ),
        { ssr: false },
    ),
    'DeliveryTrackingModal.Title': DeliveryTrackingModal.Title,
    'DeliveryTrackingModal.Description': DeliveryTrackingModal.Description,
    'DeliveryTrackingModal.CloseButton': DeliveryTrackingModal.CloseButton,
    'FavoriteItemsModal.Wrapper': FavoriteItemsModal.Wrapper,
    'FavoriteItemsModal.Provider': FavoriteItemsModal.Provider,
    'FavoriteItemsModal.Title': FavoriteItemsModal.Title,
    'FavoriteItemsModal.Content': FavoriteItemsModal.Content,
    'FavoriteItemsModal.Button': FavoriteItemsModal.Button,
    'FavoriteItemsModal.Item': FavoriteItemsModal.Item,
    'MyAccountModal.Wrapper': MyAccountModal.Wrapper,
    'MyAccountModal.Provider': MyAccountModal.Provider,
    'MyAccountModal.Title': MyAccountModal.Title,
    'MyAccountModal.UserAccount': MyAccountModal.UserAccount,
    'MyAccountModal.AddressSelect': MyAccountModal.AddressSelect,
    'MyAccountModal.FavoriteItemsMenuItem': MyAccountModal.FavoriteItemsMenuItem,
    'MyAccountModal.OrdersHistoryMenuItem': MyAccountModal.OrdersHistoryMenuItem,
    'MyAccountModal.LogoutButton': MyAccountModal.LogoutButton,
    'MyAccountModal.MenuItem': MyAccountModal.MenuItem,
    'MyCouponsModal.Wrapper': MyCouponsModal.Wrapper,
    'MyCouponsModal.Provider': MyCouponsModal.Provider,
    'MyCouponsModal.Title': MyCouponsModal.Title,
    'MyCouponsModal.Content': MyCouponsModal.Content,
    'MyCouponsModal.CouponItem': MyCouponsModal.CouponItem,
    'AddEditAddressModal.Provider': AddEditAddressModal.Provider,
    'AddEditAddressModal.Title': AddEditAddressModal.Title,
    'AddEditAddressModal.Form': AddEditAddressModal.Form,
    'AddEditAddressModal.AddressField': AddEditAddressModal.AddressField,
    'AddEditAddressModal.NameLabel': AddEditAddressModal.NameLabel,
    'AddEditAddressModal.NameDescription': AddEditAddressModal.NameDescription,
    'AddEditAddressModal.IconDropdown': AddEditAddressModal.IconDropdown,
    'AddEditAddressModal.AddressTypeSelect': AddEditAddressModal.AddressTypeSelect,
    'AddEditAddressModal.SubmitButton': AddEditAddressModal.SubmitButton,
    'AddEditAddressModal.DeleteButton': AddEditAddressModal.DeleteButton,
    'EditAccountDetailsModal.Provider': EditAccountDetailsModal.Provider,
    'EditAccountDetailsModal.Title': EditAccountDetailsModal.Title,
    'EditAccountDetailsModal.Form': EditAccountDetailsModal.Form,
    'EditAccountDetailsModal.UserAccount': EditAccountDetailsModal.UserAccount,
    'EditAccountDetailsModal.FirstNameField': EditAccountDetailsModal.FirstNameField,
    'EditAccountDetailsModal.LastNameField': EditAccountDetailsModal.LastNameField,
    'EditAccountDetailsModal.BirthDateField': EditAccountDetailsModal.BirthDateField,
    'EditAccountDetailsModal.PhoneNumberField': EditAccountDetailsModal.PhoneNumberField,
    'EditAccountDetailsModal.EmailField': EditAccountDetailsModal.EmailField,
    'EditAccountDetailsModal.NewsletterField': EditAccountDetailsModal.NewsletterField,
    'EditAccountDetailsModal.SaveButton': EditAccountDetailsModal.SaveButton,
    'SelectVenueModal.Provider': SelectVenueModal.Provider,
    'SelectVenueModal.Title': SelectVenueModal.Title,
    'SelectVenueModal.SearchFields': SelectVenueModal.SearchFields,
    'SelectVenueModal.VenuesList': SelectVenueModal.VenuesList,
    'SelectVenueModal.BottomPanel': SelectVenueModal.BottomPanel,
    'OrdersHistoryModal.Wrapper': OrdersHistoryModal.Wrapper,
    'OrdersHistoryModal.Title': OrdersHistoryModal.Title,
    'OrdersHistoryModal.Content': OrdersHistoryModal.Content,
    'ReviewModal.Wrapper': ReviewModal.Wrapper,
    'ReviewModal.Provider': ReviewModal.Provider,
    'ReviewModal.Title': ReviewModal.Title,
    'ReviewModal.Form': ReviewModal.Form,
    'ReviewModal.StarRating': ReviewModal.StarRating,
    'ReviewModal.TextArea': ReviewModal.TextArea,
    'ReviewModal.PointsMessageButton': ReviewModal.PointsMessageButton,
    'ReviewModal.SubmitButton': ReviewModal.SubmitButton,
    'NewsModal.Wrapper': NewsModal.Wrapper,
    'NewsModal.Provider': NewsModal.Provider,
    'NewsModal.Media': NewsModal.Media,
    'NewsModal.Title': NewsModal.Title,
    'NewsModal.Description': NewsModal.Description,
    'NewsModal.Badge': NewsModal.Badge,
    'NewsModal.BottomPanel': NewsModal.BottomPanel,
    'NewsModal.FixedBottomPanel': NewsModal.FixedBottomPanel,
    'NewsModal.OpenDetail': NewsModal.OpenDetail,
    'TopUpOrderModal.Wrapper': TopUpOrderModal.Wrapper,
    'TopUpOrderModal.Provider': TopUpOrderModal.Provider,
    'TopUpOrderModal.Title': TopUpOrderModal.Title,
    'TopUpOrderModal.Description': TopUpOrderModal.Description,
    'TopUpOrderModal.Carousel': TopUpOrderModal.Carousel,
    'TopUpOrderModal.Link': TopUpOrderModal.Link,
    'VerifyPhoneNumberModal.Provider': VerifyPhoneNumberModal.Provider,
    'VerifyPhoneNumberModal.Title': VerifyPhoneNumberModal.Title,
    'VerifyPhoneNumberModal.Description': VerifyPhoneNumberModal.Description,
    'VerifyPhoneNumberModal.Form': VerifyPhoneNumberModal.Form,
    'VerifyPhoneNumberModal.CodeValidity': VerifyPhoneNumberModal.CodeValidity,
    'VerifyPhoneNumberModal.VerifyButton': VerifyPhoneNumberModal.VerifyButton,
    'OrderModal.Wrapper': OrderModal.Wrapper,
    'OrderModal.Provider': OrderModal.Provider,
    'OrderModal.Title': OrderModal.Title,
    'OrderModal.Info': OrderModal.Info,
    'OrderModal.Details': OrderModal.Details,
    'OrderModal.BuyAgainButton': OrderModal.BuyAgainButton,
    'LoginModal.Wrapper': LoginModal.Wrapper,
    'LoginModal.Provider': LoginModal.Provider,
    'LoginModal.Title': LoginModal.Title,
    'LoginModal.Form': LoginModal.Form,
    'LoginModal.EmailField': LoginModal.EmailField,
    'LoginModal.PasswordField': LoginModal.PasswordField,
    'LoginModal.ForgotPasswordButton': LoginModal.ForgotPasswordButton,
    'LoginModal.SubmitButton': LoginModal.SubmitButton,
    'LoginModal.RegisterText': LoginModal.RegisterText,
    'LoginModal.BottomPanel': LoginModal.BottomPanel,
    'RegisterModal.Wrapper': RegisterModal.Wrapper,
    'RegisterModal.Provider': RegisterModal.Provider,
    'RegisterModal.Title': RegisterModal.Title,
    'RegisterModal.Form': RegisterModal.Form,
    'RegisterModal.EmailField': RegisterModal.EmailField,
    'RegisterModal.PasswordField': RegisterModal.PasswordField,
    'RegisterModal.AcceptTermsCheckbox': RegisterModal.AcceptTermsCheckbox,
    'RegisterModal.SubmitButton': RegisterModal.SubmitButton,
    'RegisterModal.LoginText': RegisterModal.LoginText,
    'RegisterModal.BottomPanel': RegisterModal.BottomPanel,
    'ResetPasswordModal.Wrapper': ResetPasswordModal.Wrapper,
    'ResetPasswordModal.Provider': ResetPasswordModal.Provider,
    'ResetPasswordModal.Title': ResetPasswordModal.Title,
    'ResetPasswordModal.Form': ResetPasswordModal.Form,
    'ResetPasswordModal.EmailField': ResetPasswordModal.EmailField,
    'ResetPasswordModal.SubmitButton': ResetPasswordModal.SubmitButton,
    'ResetPasswordModal.BottomPanel': ResetPasswordModal.BottomPanel,
    'SetNewPasswordModal.Wrapper': SetNewPasswordModal.Wrapper,
    'SetNewPasswordModal.Provider': SetNewPasswordModal.Provider,
    'SetNewPasswordModal.Title': SetNewPasswordModal.Title,
    'SetNewPasswordModal.Form': SetNewPasswordModal.Form,
    'SetNewPasswordModal.PasswordField': SetNewPasswordModal.PasswordField,
    'SetNewPasswordModal.SubmitButton': SetNewPasswordModal.SubmitButton,
    'SetNewPasswordModal.RememberPasswordText': SetNewPasswordModal.RememberPasswordText,
    'SetNewPasswordModal.BottomPanel': SetNewPasswordModal.BottomPanel,
    BackButton,
};

export type PageComponentData = {
    prefab?: keyof typeof PrefabRegistry;
    component?: keyof typeof ComponentRegistry;
    options?: Record<string, unknown>;
};

export type CustomPrefabs = Record<
    string,
    {
        defaultVariant?: string;
        /** When false, a MENU item in a column variant stays a column. Steps variant ignores this. */
        menuUsesSteps?: boolean;
        prefabs?: Record<string, { options?: Record<string, unknown>; children?: PageComponentData[] }>;
    }
>;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return (
        value !== null && typeof value === 'object' && !Array.isArray(value) && !React.isValidElement(value)
    );
};

const mergeDeep = (target: Record<string, unknown>, source: Record<string, unknown>) => {
    const result: Record<string, unknown> = { ...target };
    for (const [key, value] of Object.entries(source)) {
        if (isPlainObject(result[key]) && isPlainObject(value)) {
            result[key] = mergeDeep(result[key] as Record<string, unknown>, value);
        } else {
            result[key] = value;
        }
    }
    return result;
};

const resolveValue = (
    value: unknown,
    contextOptions: Record<string, unknown>,
    options?: { skipKeys?: Set<string>; originalOptions?: Record<string, unknown> },
): any /* eslint-disable-line @typescript-eslint/no-explicit-any */ => {
    const resolvePath = (path: string, source: Record<string, unknown>) => {
        if (path === '*' || path === '') {
            return source;
        }

        // Simple path resolution (e.g. "storageKeys.categoryId")
        return path.split('.').reduce((acc, part) => {
            if (acc !== null && typeof acc === 'object') {
                return (acc as Record<string, unknown>)[part];
            }
            return undefined;
        }, source as unknown);
    };

    const resolveOptionsPath = (path: string) => {
        if (path === 'original' || path.startsWith('original.')) {
            const originalOptions = options?.originalOptions ?? {};
            const originalPath = path === 'original' ? '' : path.replace(/^original\./, '');
            return resolvePath(originalPath, originalOptions);
        }

        return resolvePath(path, contextOptions);
    };

    if (typeof value === 'string' && value.startsWith('options:')) {
        const path = value.replace('options:', '');
        return resolveOptionsPath(path);
    }

    if (Array.isArray(value)) {
        return value.map((v) => resolveValue(v, contextOptions, options));
    }

    if (value !== null && typeof value === 'object') {
        const resolved: Record<string, unknown> = {};
        // Preserve children from original options if they exist
        const originalChildren = 'children' in value ? value.children : undefined;
        for (const [k, v] of Object.entries(value)) {
            if (options?.skipKeys?.has(k)) {
                resolved[k] = v;
                continue;
            }
            if (k === '__inherit' || k === '__inherits') {
                const inherited = resolveValue(v, contextOptions, options);
                if (isPlainObject(inherited)) {
                    Object.assign(resolved, mergeDeep(resolved, inherited));
                }
                continue;
            }
            if (v === 'options:*') {
                Object.assign(resolved, mergeDeep(resolved, contextOptions));
                // Restore children from original options if they were preserved
                if (originalChildren !== undefined && !('children' in resolved)) {
                    resolved.children = originalChildren;
                }
            } else {
                const nextValue = resolveValue(v, contextOptions, options);
                if (isPlainObject(resolved[k]) && isPlainObject(nextValue)) {
                    resolved[k] = mergeDeep(resolved[k] as Record<string, unknown>, nextValue);
                } else {
                    resolved[k] = nextValue;
                }
            }
        }
        return resolved;
    }

    return value;
};

/**
 * Checks if a value is an array of nested component definitions
 */
const isComponentDefinition = (value: unknown): value is PageComponentData => {
    return (
        typeof value === 'object' &&
        value !== null &&
        ('component' in value || 'prefab' in value) &&
        !('position' in value) &&
        !('index' in value) &&
        !('id' in value)
    );
};

const isNestedComponentsArray = (value: unknown): value is PageComponentData[] => {
    return Array.isArray(value) && value.length > 0 && value.every((item) => isComponentDefinition(item));
};

/**
 * Processes options and converts nested component arrays to React nodes
 * Excludes 'children' as they are handled separately
 */
const processOptionsWithNestedComponents = (
    options: Record<string, unknown> | undefined | null,
    contextOptions: Record<string, unknown>,
    slots?: {
        body?: PageComponentData[];
    },
    customPrefabs?: CustomPrefabs,
    templatePrefabs?: CustomPrefabs,
): Record<string, unknown> => {
    if (!options || typeof options !== 'object') {
        return {};
    }

    const processValue = (value: unknown, parentKey?: string): unknown => {
        if (isNestedComponentsArray(value)) {
            return (
                <PrefabRenderer
                    data={value as PageComponentData[]}
                    contextOptions={contextOptions}
                    slots={slots}
                    templatePrefabs={templatePrefabs}
                    customPrefabs={customPrefabs}
                />
            );
        }

        if (Array.isArray(value)) {
            return value.map((item, itemIndex) => {
                if (isComponentDefinition(item)) {
                    return (
                        <PrefabRenderer
                            key={`${parentKey ?? 'item'}-${itemIndex}`}
                            data={[item]}
                            contextOptions={contextOptions}
                            slots={slots}
                            templatePrefabs={templatePrefabs}
                            customPrefabs={customPrefabs}
                        />
                    );
                }
                return processValue(item, parentKey);
            });
        }

        if (React.isValidElement(value)) {
            return value;
        }

        if (value !== null && typeof value === 'object') {
            const processedObject: Record<string, unknown> = {};
            for (const [innerKey, innerValue] of Object.entries(value)) {
                processedObject[innerKey] = processValue(innerValue, innerKey);
            }
            return processedObject;
        }

        return value;
    };

    const processed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(options)) {
        if (key === 'children') {
            processed[key] = value;
            continue;
        }
        // Don't process categoryOverrides and categoryInjections here - they need to be processed
        // inside CategoryProvider to have access to CategoryContext
        if (key === 'categoryOverrides' || key === 'categoryInjections') {
            processed[key] = value;
            continue;
        }
        processed[key] = processValue(value, key);
    }

    return processed;
};

/**
 * Creates a merged prefab registry combining default PrefabRegistry with custom prefabs from config
 * Custom prefabs are merged with defaults using destructuring - default values are preserved if not overridden
 */
const createMergedPrefabRegistry = (
    customPrefabs?: CustomPrefabs,
    baseRegistry: typeof PrefabRegistry = PrefabRegistry,
): typeof PrefabRegistry => {
    if (!customPrefabs || Object.keys(customPrefabs).length === 0) {
        return baseRegistry;
    }

    const merged = { ...baseRegistry };

    // Merge custom prefabs into the registry using destructuring
    // Default values are preserved if not explicitly overridden
    // Supports multiple variants (not just "default")
    for (const [key, customPrefab] of Object.entries(customPrefabs)) {
        const defaultPrefab = baseRegistry[key as keyof typeof PrefabRegistry];

        if (defaultPrefab) {
            const defaultPrefabStructure = defaultPrefab as {
                defaultVariant?: string;
                prefabs?: Record<
                    string,
                    { options?: Record<string, unknown>; children: PageComponentData[] }
                >;
            };

            let mergedPrefabs = defaultPrefabStructure.prefabs;

            if (customPrefab.prefabs) {
                // Merge all variants from custom prefab
                const mergedVariants: Record<
                    string,
                    { options?: Record<string, unknown>; children: PageComponentData[] }
                > = {
                    ...(defaultPrefabStructure.prefabs ?? {}),
                };

                // Merge each custom variant with its default counterpart (if exists)
                for (const [variantName, customVariant] of Object.entries(customPrefab.prefabs)) {
                    const defaultVariant = defaultPrefabStructure.prefabs?.[variantName];

                    mergedVariants[variantName] = {
                        options: defaultVariant?.options
                            ? mergeDeep({ ...defaultVariant.options }, customVariant.options || {})
                            : customVariant.options,
                        children: customVariant.children ?? defaultVariant?.children ?? [],
                    };
                }

                mergedPrefabs = mergedVariants;
            }

            if (mergedPrefabs && customPrefab.menuUsesSteps !== undefined) {
                mergedPrefabs = Object.fromEntries(
                    Object.entries(mergedPrefabs).map(([variantName, variant]) => [
                        variantName,
                        {
                            ...variant,
                            options: { ...variant.options, menuUsesSteps: customPrefab.menuUsesSteps },
                        },
                    ]),
                );
            }

            merged[key as keyof typeof PrefabRegistry] = {
                ...defaultPrefab,
                defaultVariant: customPrefab.defaultVariant ?? defaultPrefabStructure.defaultVariant,
                prefabs: mergedPrefabs,
            } as any; /* eslint-disable-line @typescript-eslint/no-explicit-any */
        } else if (customPrefab.prefabs) {
            // If no default prefab exists, use custom prefab as-is
            merged[key as keyof typeof PrefabRegistry] =
                customPrefab as any; /* eslint-disable-line @typescript-eslint/no-explicit-any */
        }
    }

    return merged;
};

export const PrefabRenderer = ({
    data,
    contextOptions = {},
    slots,
    templatePrefabs,
    customPrefabs,
    isolate = false,
    preview = false,
}: {
    data: PageComponentData[];
    contextOptions?: Record<string, unknown>;
    slots?: {
        body?: PageComponentData[];
    };
    templatePrefabs?: CustomPrefabs;
    customPrefabs?: CustomPrefabs;
    /**
     * Wrap each top-level child in an error boundary so one broken section does not
     * blank the whole page. Set only at page-level call sites; intentionally not
     * propagated into a prefab's own expanded children (only into the body slot).
     */
    isolate?: boolean;
    /** Editor/preview mode — boundaries show a visible placeholder instead of skipping. */
    preview?: boolean;
}) => {
    // Create merged registry: default → template → user overrides
    const templateMerged = createMergedPrefabRegistry(templatePrefabs);
    const mergedPrefabRegistry = createMergedPrefabRegistry(customPrefabs, templateMerged);

    return (
        <>
            {data.map((child, index) => {
                const element = (() => {
                    const resolvedChildOptions = resolveValue(child.options, contextOptions, {
                        skipKeys: new Set(['categoryOverrides', 'categoryInjections']),
                    }) as Record<string, unknown>;

                    if (child.prefab === 'Slot') {
                        if (resolvedChildOptions?.type === 'body') {
                            return (
                                <PrefabRenderer
                                    key={index}
                                    data={slots?.body ?? []}
                                    contextOptions={contextOptions}
                                    slots={slots}
                                    templatePrefabs={templatePrefabs}
                                    customPrefabs={customPrefabs}
                                    isolate={isolate}
                                    preview={preview}
                                />
                            );
                        }
                        return null;
                    }

                    // Parse prefab name and variant (e.g., "Center:variant-1" -> prefabName="Center", variant="variant-1")
                    const prefabName = child.prefab?.includes(':')
                        ? child.prefab.split(':')[0]
                        : child.prefab;
                    const variant = child.prefab?.includes(':') ? child.prefab.split(':')[1] : undefined;

                    const prefab = prefabName
                        ? mergedPrefabRegistry[prefabName as keyof typeof PrefabRegistry]
                        : null;
                    const Component = child.component
                        ? (ComponentRegistry[child.component] as React.ComponentType<{
                              children?: React.ReactNode;
                          }>)
                        : null;

                    if (prefab) {
                        const prefabStructure = prefab as {
                            defaultVariant?: string;
                            prefabs?: Record<
                                string,
                                { options?: Record<string, unknown>; children: PageComponentData[] }
                            >;
                        };

                        // Use specified variant or fallback to defaultVariant (or "default")
                        const fallbackVariantKey = prefabStructure.defaultVariant ?? 'default';
                        const prefabVariant =
                            prefabStructure.prefabs?.[variant ?? fallbackVariantKey] ??
                            prefabStructure.prefabs?.[fallbackVariantKey] ??
                            prefabStructure.prefabs?.default;

                        if (!prefabVariant) {
                            // Variant not found, skip rendering this prefab
                            return null;
                        }

                        const prefabDefaults = prefabVariant;
                        const instanceOptions = {
                            ...(prefabDefaults.options || {}),
                            ...(resolvedChildOptions || {}),
                        };

                        // Expand prefab and resolve options
                        const expandedData = prefabDefaults.children.map((pChild: PageComponentData) => {
                            const resolvedOptions = resolveValue(pChild.options, instanceOptions, {
                                originalOptions: resolvedChildOptions,
                            }) as Record<string, unknown>;

                            return {
                                ...pChild,
                                options: resolvedOptions,
                            };
                        });

                        const expandedContent = (
                            <PrefabRenderer
                                data={expandedData as PageComponentData[]}
                                contextOptions={instanceOptions}
                                templatePrefabs={templatePrefabs}
                                customPrefabs={customPrefabs}
                            />
                        );

                        // Process options to handle nested components in any option
                        const processedOptions = processOptionsWithNestedComponents(
                            instanceOptions,
                            instanceOptions,
                            slots,
                            customPrefabs,
                            templatePrefabs,
                        );

                        if (Component) {
                            return (
                                <Component
                                    key={index}
                                    {
                                        ...(processedOptions as any) /* eslint-disable-line @typescript-eslint/no-explicit-any */
                                    }
                                >
                                    {expandedContent}
                                </Component>
                            );
                        }

                        return <Fragment key={index}>{expandedContent}</Fragment>;
                    }

                    if (Component) {
                        // Process options to handle nested components in any option
                        const processedOptions = processOptionsWithNestedComponents(
                            resolvedChildOptions || {},
                            resolvedChildOptions || {},
                            slots,
                            customPrefabs,
                            templatePrefabs,
                        );

                        return (
                            <Component
                                key={index}
                                {
                                    ...(processedOptions as any) /* eslint-disable-line @typescript-eslint/no-explicit-any */
                                }
                            >
                                {resolvedChildOptions?.children &&
                                    Array.isArray(resolvedChildOptions.children) && (
                                        <PrefabRenderer
                                            data={resolvedChildOptions.children as PageComponentData[]}
                                            contextOptions={resolvedChildOptions}
                                            slots={slots}
                                            templatePrefabs={templatePrefabs}
                                            customPrefabs={customPrefabs}
                                        />
                                    )}
                            </Component>
                        );
                    }

                    // Fallback for cases where it's just a list of children
                    if (resolvedChildOptions?.children && Array.isArray(resolvedChildOptions.children)) {
                        return (
                            <PrefabRenderer
                                key={index}
                                data={resolvedChildOptions.children as PageComponentData[]}
                                contextOptions={resolvedChildOptions}
                                slots={slots}
                                templatePrefabs={templatePrefabs}
                                customPrefabs={customPrefabs}
                            />
                        );
                    }

                    return null;
                })();

                if (!isolate || element === null) return element;

                return (
                    <PrefabErrorBoundary
                        key={index}
                        preview={preview}
                        label={child.prefab ?? child.component ?? undefined}
                    >
                        {element}
                    </PrefabErrorBoundary>
                );
            })}
        </>
    );
};

registerVenueCardRenderer(PrefabRenderer);
